import { useState, useRef, useEffect } from 'react';
import { Bell, CheckCheck, Settings } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

// Guard against open-redirects / javascript: URLs: only allow same-origin relative paths.
function safeInternalPath(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return null;
  }
}

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  data?: Record<string, unknown>;
  action_url?: string;
  read_at?: string;
  created_at: string;
}

interface NotificationListResponse {
  data: Notification[];
  meta: {
    has_more: boolean;
  };
}

interface UnreadCountResponse {
  unread_count: number;
}

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [enableUnreadCount, setEnableUnreadCount] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch unread count
  const { data: unreadData } = useQuery<UnreadCountResponse>({
    queryKey: ['notifications', user?.id, 'unread-count'],
    queryFn: async () => {
      return api.get<UnreadCountResponse>('/api/v1/notifications/unread-count');
    },
    enabled: enableUnreadCount || isOpen,
    refetchInterval: enableUnreadCount ? 30000 : false, // Poll every 30 seconds once enabled
  });

  // Fetch recent notifications
  const { data: notificationsData } = useQuery<NotificationListResponse>({
    queryKey: ['notifications', user?.id, 'recent'],
    queryFn: async () => {
      return api.get<NotificationListResponse>('/api/v1/notifications', {
        params: { limit: '10' },
      });
    },
    enabled: isOpen, // Only fetch when dropdown is open
  });

  // Mark as read mutation
  const markAsRead = useMutation({
    mutationFn: async (notificationId: string) => {
      await api.post(`/api/v1/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all as read mutation
  const markAllAsRead = useMutation({
    mutationFn: async () => {
      await api.post('/api/v1/notifications/read-all');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const unreadCount = unreadData?.unread_count || 0;
  const notifications = notificationsData?.data || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Delay unread count fetch to avoid blocking initial page load
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setEnableUnreadCount(true);
    }, 1500);
    return () => window.clearTimeout(timer);
  }, []);

  // Get icon for notification type
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'alert_critical':
        return '🔴';
      case 'alert_warning':
        return '⚠️';
      case 'edit_request_approved':
        return '✅';
      case 'edit_request_rejected':
        return '❌';
      case 'edit_request_new':
        return '📝';
      case 'missed_entry':
        return '📋';
      default:
        return '📣';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex min-h-11 min-w-11 items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllAsRead.mutate()}
                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  title="Mark all as read"
                >
                  <CheckCheck className="h-4 w-4" />
                </button>
              )}
              <Link
                to="/settings"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setIsOpen(false)}
                title="Notification settings"
              >
                <Settings className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <Bell className="h-8 w-8 text-gray-300 mb-2" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notification.read_at ? 'bg-primary-50' : ''
                  }`}
                  onClick={() => {
                    if (!notification.read_at) {
                      markAsRead.mutate(notification.id);
                    }
                    const safePath = safeInternalPath(notification.action_url);
                    if (safePath) {
                      navigate({ to: safePath });
                    }
                    setIsOpen(false);
                  }}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{getTypeIcon(notification.type)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {notification.title}
                      </p>
                      <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                        {notification.body}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    {!notification.read_at && (
                      <div className="w-2 h-2 rounded-full bg-primary-500 mt-1.5 flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <Link
            to="/notifications"
            className="block text-center py-3 text-sm text-primary-600 hover:bg-gray-50 border-t border-gray-100"
            onClick={() => setIsOpen(false)}
          >
            View all notifications
          </Link>
        </div>
      )}
    </div>
  );
}
